import React, { useEffect, useState } from 'react';
import { InteractiveTooltip, Button, COLORS } from '@livecommerce/design-system';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { useTooltipContext } from 'contexts/TooltipContext.jsx';
import { trackingService } from 'services/tracking/TrackingService.js';
import { styled } from '@linaria/react';

const StepTooltip = ({
  tooltipKey,
  title,
  description,
  step,
  maxSteps,
  position,
  onNext,
  onPrevious,
  onLast,
  isVisible: isTooltipVisible,
  children,
  ...props
}) => {
  const { t } = useLocalization();

  const {
    [tooltipKey]: tooltipData,
    isStepTooltipVisible,
    updateTooltipContext,
    updateStepTooltipContext,
  } = useTooltipContext();

  const { step: contextStep } = tooltipData;
  const _isStepTooltipVisible = () => isStepTooltipVisible(tooltipKey, step) && isTooltipVisible;
  const [isVisible, setIsVisible] = useState(_isStepTooltipVisible);

  useEffect(() => {
    setIsVisible(isStepTooltipVisible(tooltipKey, step));
  }, [contextStep]);

  const actions = () => {
    const previousStep = () => {
      trackingService.track({
        eventType: `${tooltipKey}:stepTooltip-previous`,
      });
      updateStepTooltipContext(tooltipKey, 'previous');
      setIsVisible(false);
      if (typeof onPrevious === 'function') onPrevious();
    };

    const nextStep = () => {
      trackingService.track({
        eventType: `${tooltipKey}:stepTooltip-next`,
      });
      updateStepTooltipContext(tooltipKey, 'next');
      setIsVisible(false);
      if (typeof onNext === 'function') onNext();
    };

    const lastStep = () => {
      trackingService.track({
        eventType: `${tooltipKey}:stepTooltip-last`,
      });
      closeTooltip();
      if (typeof onLast === 'function') onLast();
    };

    return (
      <div>
        {maxSteps > 1 && (
          <ProgressRow>
            <ProgressLineRow>
              <ProgressLine style={{ width: `${(step / maxSteps) * 100}%` }} />
            </ProgressLineRow>
            <span>{`${step}/${maxSteps}`}</span>
          </ProgressRow>
        )}
        <ActionsRow>
          {step === 1 && (
            <>
              <Button
                variant="hollow"
                theme="dark"
                type="button"
                onClick={closeTooltip}
                style={{ flex: 1 }}
              >
                {t('stepTooltip.remindMe')}
              </Button >
              <Button
                variant="solid"
                theme="dark"
                type="button"
                onClick={nextStep}
                style={{ flex: 1 }}
              >
                {t('stepTooltip.nextStep')}
              </Button >
            </>
          )}
          {maxSteps > 1 && step > 1 && maxSteps > step && (
            <>
              <Button
                variant="hollow"
                theme="dark"
                type="button"
                onClick={previousStep}
                style={{ flex: 1 }}
              >
                {t('stepTooltip.previousStep')}
              </Button >
              <Button
                variant="solid"
                theme="dark"
                type="button"
                onClick={nextStep}
                style={{ flex: 1 }}
              >
                {t('stepTooltip.nextStep')}
              </Button >
            </>
          )}
          {maxSteps > 1 && step > 1 && (maxSteps === step) && (
            <>
              <Button
                variant="hollow"
                theme="dark"
                type="button"
                onClick={previousStep}
                style={{ flex: 1 }}
              >
                {t('stepTooltip.previousStep')}
              </Button >
              <Button
                variant="solid"
                theme="dark"
                type="button"
                onClick={lastStep}
                style={{ flex: 1 }}
              >
                {t('stepTooltip.lastStep')}
              </Button >
            </>
          )}
        </ActionsRow>
      </div>
    );
  };

  const closeTooltip = () => {
    updateTooltipContext(tooltipKey);
    setIsVisible(false);
  };

  return (
    <InteractiveTooltip
      title={title}
      description={description}
      content={actions()}
      variant="orange"
      tooltipPosition={position}
      onClose={closeTooltip}
      isShown={isVisible}
      minWidth="300px"
      {...props}
    >
      {children}
    </InteractiveTooltip>
  );
};


const ActionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  gap: 12px;
`;

const ProgressRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  margin-top: 8px;
  & > span {
    font-size: 12px;
    line-height: 16px;
  }
`;

const ProgressLineRow = styled.div`
  width: 100%;
  height: 2px;
  gap: 4px;
  background-color: ${COLORS.light_500};
  border-radius: 4px;
`;

const ProgressLine = styled.div`
  height: 2px;
  background-color: ${COLORS.light_000};
  border-radius: 4px;
`;

export default StepTooltip;
